/* eslint-disable no-lonely-if */
import { values } from "lodash";

import untypedItems from "@data/items.json";

import type { Item, ItemsJson } from "@typings/product/item";
import { Product } from "@typings/product/product";

const items: ItemsJson = untypedItems as ItemsJson;

export interface OptimalUpsell {
  item: Item;
  variant: {
    offerId: number;
    name: string;
    price: number;
  };
  giftTarget: string;
  amountNeeded: number;
  totalAfterAdd: number;

  isUpgrade?: boolean;
  additionalUpsell?: {
    item: Item;
    variant: {
      offerId: number;
      name: string;
      price: number;
    };
  };
}

/**
 * Calculates the optimal upsell to reach the next gift tier
 * Based on the requirements:
 * - Non-subscription: Below $40 → upsell to unlock Joint Care Treats ($40)
 * - Subscription: Below $75 → upsell to unlock Blanket ($75)
 * - Subscription: $75–$109 → upsell to unlock Bed ($110)
 * - $110+ → no more gift-driven upsells
 */
export const getOptimalUpsell = (cartItems: Product[]): OptimalUpsell | null => {
  const nonGiftItems = cartItems.filter((ci) => ci.item.category.name !== "Free Gift");

  const totalPrice = nonGiftItems.reduce(
    (acc, { configuration }) => acc + configuration.price * configuration.quantity,
    0
  );

  const hasSubscription = nonGiftItems.some(
    (ci) => ci.configuration.offer.name === "Monthly Subscription"
  );

  let giftTarget: string;
  let threshold: number;

  if (totalPrice < 40 && !hasSubscription) {
    giftTarget = "Free Joint Care Treats";
    threshold = 40;
  } else if (totalPrice < 75) {
    giftTarget = "Free Calming Blanket";
    threshold = 75;
  } else if (totalPrice < 110) {
    giftTarget = "Free Calming Dog Bed 🛌";
    threshold = 110;
  } else {
    return null;
  }

  const amountNeeded = threshold - totalPrice;

  let bestUpsell: OptimalUpsell | null = null;
  let bestPrice = Infinity;

  for (const cartItem of nonGiftItems) {
    const { item } = cartItem;

    if (cartItem.configuration.quantity === 1) {
      const threeJarVariant = item.variants.find(
        (v) => v.name.toLowerCase().includes("3") && v.offerId === cartItem.configuration.offer.id
      );

      if (threeJarVariant) {
        const currentTotal = cartItem.configuration.price * cartItem.configuration.quantity;
        const newTotal = threeJarVariant.price;
        const priceDifference = newTotal - currentTotal;
        const totalAfterUpgrade = totalPrice - currentTotal + newTotal;

        if (totalAfterUpgrade >= threshold) {
          const overage = totalAfterUpgrade - threshold;

          if (overage < bestPrice) {
            bestUpsell = {
              item,
              variant: {
                offerId: threeJarVariant.offerId,
                name: threeJarVariant.name,
                price: priceDifference,
              },
              giftTarget,
              amountNeeded,
              totalAfterAdd: totalAfterUpgrade,
              isUpgrade: true,
            };
            bestPrice = overage;
          }
        } else {
          const upsellItems = values(items).filter((i) => i.tags.some((t) => t.name === "Upsell"));
          const offerId = hasSubscription ? 2 : 1;

          for (const upsellItem of upsellItems) {
            if (cartItems.some((ci) => ci.item.id === upsellItem.id)) {
              continue;
            }

            const variants = upsellItem.variants.filter((v) => v.offerId === offerId);

            for (const variant of variants) {
              const totalAfterBoth = totalAfterUpgrade + variant.price;
              const overage = totalAfterBoth - threshold;

              if (totalAfterBoth >= threshold && overage < bestPrice) {
                bestUpsell = {
                  item,
                  variant: {
                    offerId: threeJarVariant.offerId,
                    name: threeJarVariant.name,
                    price: priceDifference,
                  },
                  giftTarget,
                  amountNeeded,
                  totalAfterAdd: totalAfterBoth,
                  isUpgrade: true,
                  additionalUpsell: {
                    item: upsellItem,
                    variant,
                  },
                };
                bestPrice = overage;
              }
            }
          }
        }
      }
    }
  }

  if (!bestUpsell) {
    const upsellItems = values(items).filter((i) => i.tags.some((t) => t.name === "Upsell"));
    const offerId = hasSubscription ? 2 : 1;

    for (const item of upsellItems) {
      if (cartItems.some((ci) => ci.item.id === item.id)) {
        continue;
      }

      const variants = item.variants.filter((v) => v.offerId === offerId);

      for (const variant of variants) {
        const totalAfterAdd = totalPrice + variant.price;

        if (totalAfterAdd >= threshold) {
          const overage = totalAfterAdd - threshold;

          if (overage < bestPrice) {
            bestUpsell = {
              item,
              variant,
              giftTarget,
              amountNeeded,
              totalAfterAdd,
              isUpgrade: false,
            };
            bestPrice = overage;
          }
        }
      }
    }
  }

  return bestUpsell;
};
